import { ReactElement, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Button from '../../../controls/Button';
import DevicesTable from '../../DevicesTable';
import AddDeviceModal from '../../../modals/AddDevice';
import AuthHeader from '../header';
import { useData } from '../../../context/data.context';
import { ActionData, DeviceAttributes } from '../../../constants/telosair-interfaces';
import { deleteDevice } from '../../../services/dashboard-service';
import { TOAST_MESSAGES } from '../../../constants/global-constants';
import { GA_CATEGORIES, sendCustomEventToGa } from '../../../_helpers/ga';

/**
 * Device setup component for account activation.
 * @returns - React compoennt for device setup.
 */
export default function AccountActivationDeviceSetup(): ReactElement {
  const { devices, fetchAllDevices } = useData();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [, setIsLoading] = useState(false);
  const [actionData, setActionData] = useState<ActionData<DeviceAttributes>>(
    {} as ActionData<DeviceAttributes>
  );
  const navigate = useNavigate();
  /**
   * Function to handle Device Modal.
   * @param newDeviceAdded - To check weather a New device is added or not.
   */
  const handleDeviceModalClose = async (newDeviceAdded: boolean): Promise<void> => {
    try {
      if (actionData.type === 'edit') {
        sendCustomEventToGa(GA_CATEGORIES.onboarding, 'device-editing-completed');
      }
      if (newDeviceAdded) {
        sendCustomEventToGa(GA_CATEGORIES.onboarding, 'device-added');
        await fetchAllDevices();
      }
      sendCustomEventToGa(GA_CATEGORIES.onboarding, 'device-modal-closed');
      setIsModalOpen(false);
      setActionData({} as ActionData<DeviceAttributes>);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  /**
   * Function to delete a group.
   * @param id - ID of the group.
   */
  const handleDeleteDevice = async (id: number): Promise<void> => {
    try {
      setIsLoading(true);
      await deleteDevice(id);
      await fetchAllDevices();
      setIsLoading(false);
      toast.success(TOAST_MESSAGES.deleted);
      sendCustomEventToGa(GA_CATEGORIES.onboarding, 'device-deletion-completed');
    } catch (error) {
      sendCustomEventToGa(GA_CATEGORIES.onboarding, 'device-deletion-failed');
      toast.error(TOAST_MESSAGES.error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  /**
   * Function to operate delete and edit group.
   * @param action - Action data needs type and group data.
   */
  const handleAction = async (action: ActionData<DeviceAttributes>): Promise<void> => {
    if (action.type === 'delete') {
      sendCustomEventToGa(GA_CATEGORIES.onboarding, 'device-deletion-initiated');
      handleDeleteDevice(action.data.id);
    } else {
      sendCustomEventToGa(GA_CATEGORIES.onboarding, 'device-editing-initiated');
      setActionData(action);
      setIsModalOpen(true);
    }
  };
  return (
    <div className="h-screen flex flex-col flex-1">
      <div className="flex flex-col justify-between sticky top-0 z-20">
        <AuthHeader />
        <div className="w-1/3 border-t-4 border-t-primary-500" />
      </div>
      <div className="h-full flex flex-1 md:block w-full">
        <div className="md:grid flex flex-col flex-1 md:container grid-cols-12 py-10 px-3 md:px-16 md:mx-auto h-full gap-3">
          <div className="col-span-2 w-full md:w-auto flex justify-center items-center md:items-start md:justify-start">
            <div className="w-4/5">
              <Button
                onClick={() => navigate(-1)}
                isBlockButton
                theme="secondary-color"
                size="sm"
                name="Go Back"
              />
            </div>
          </div>
          <div className="md:col-span-8 col-span-12 bg-container-700 p-6 rounded h-full flex flex-col max-h-[80%] md:max-h-[90%] overflow-auto">
            <div className="text-primary-500 text-2xl font-bold">
              Step 1 of 3: <span className="text-white">Device Setup</span>
            </div>
            <div className="text-gray-400 mt-1 leading-6">
              Device list includes pre-added devices with your account, add and modify <br />{' '}
              devices to take control how you want it to work
            </div>
            <div className="flex justify-between items-center mt-8 text-gray-400 text-sm">
              <div>
                Device List: <span className="text-gray-300">{devices?.length || '0'} Devices</span>{' '}
                | Selected: <span className="text-gray-300">0 Devices</span>
              </div>
              <div>
                <Button
                  onClick={() => {
                    setIsModalOpen(true);
                    sendCustomEventToGa(GA_CATEGORIES.onboarding, 'device-modal-opened');
                  }}
                  theme="secondary-gray"
                  size="sm"
                  name="Add Device"
                />
              </div>
            </div>
            <div className="mt-5 flex flex-1">
              <DevicesTable handleAction={handleAction} isActivatingDevice devices={devices} />
            </div>
          </div>
          <div className="col-span-2 flex-col md:items-end flex w-full md:w-auto items-center">
            <div className="w-4/5">
              <Button
                onClick={() => navigate('/account-activation/group-setup')}
                isBlockButton
                theme="primary"
                size="sm"
                name="Confirm Devices"
              />
            </div>
            <div className="w-4/5 mt-3">
              <Button
                onClick={() => navigate('/account-activation/group-setup')}
                isBlockButton
                theme="secondary-color"
                size="sm"
                name="Skip setup"
              />
            </div>
          </div>
        </div>
      </div>
      {isModalOpen && (
        <AddDeviceModal
          groups={[]}
          actionData={actionData}
          setIsGroupModalOpen={() => null}
          handleClose={handleDeviceModalClose}
        />
      )}
    </div>
  );
}
